<template>
  <div class="maincon">
    <div class="calendar-grid"> 
      <div
        v-for="(day, index) in weekDays"
        :key="'label-' + index"
        class="calendar-weekday"
        :class="{ 'weekend': index === 5 || index === 6 }"
      >
        {{ day }}
      </div>
 
      <div
        v-for="(day, index) in daysInMonth"
        :key="index"
        class="calendar-day"
        :class="{
          weekend: isWeekend(index),
          empty: day === null,
          today: isToday(day)
        }"
        @click="day && $emit('day-selected', day)"
      >
        <div style="text-align: center;" v-if="day">
          <strong>{{ day }}</strong>
 
          <div
            class="cal"
            v-if="monthCalories[day] !== undefined"
            :class="{
              'over': monthCalories[day] > dailycal,
              'under': monthCalories[day] <= dailycal
            }"
          >
            {{ formatCal(monthCalories[day]) }} {{ $t('noahfit.month.kcal_suffix') }}
          </div>
 
          <div v-if="monthCalories[day] !== undefined && dailycal !== null" class="diff">
            <span>{{ $t('noahfit.month.diff_label') }} </span> 
            <span
              :class="{
                over: monthCalories[day] > dailycal,
                under: monthCalories[day] < dailycal,
                equal: monthCalories[day] === dailycal
              }"
            >
              {{ formatDiff(monthCalories[day] - dailycal) }}
            </span>
          </div>
        </div>
      </div>
    </div> 
  </div>

  <div class="monthly-summary" v-if="dailycal !== null">
    <h3>{{ $t('noahfit.month.summary_title') }}</h3>
    <div><strong>{{ $t('noahfit.month.recorded_days') }}</strong> {{ recordedDaysCount }} {{ $t('noahfit.month.day_unit') }}</div>
    <div><strong>{{ $t('noahfit.month.required_total') }}</strong> {{ formatTotal(requiredMonthCal) }} {{ $t('noahfit.month.kcal_suffix') }}</div>
    <div><strong>{{ $t('noahfit.month.consumed_total') }}</strong> {{ formatTotal(totalMonthCal) }} {{ $t('noahfit.month.kcal_suffix') }}</div>
    <div><strong>{{ $t('noahfit.month.diff_total') }}</strong> 
      <span :class="{
        over: monthlyDiff > 0,
        under: monthlyDiff < 0,
        equal: monthlyDiff === 0
      }">
        {{ formatMonthlyDiff(monthlyDiff) }}
      </span>
    </div>
    <p style="margin-top: 8px; font-size: 13px;">{{ $t('noahfit.month.note') }}</p>
  </div>
  
  
</template>

<script>
import axios from 'axios';
import { eventBus } from "@/utils/eventBus";  

export default {
  emits: ['day-selected'],
  props: ['year', 'month'],
  data() {
    const today = new Date();
    return {
      todayDate: today.getDate(),
      todayMonth: today.getMonth(),
      todayYear: today.getFullYear(),
      monthCalories: {},  
      dailycal: null,
    };
  },
  computed: {
    weekDays() {
      return this.$tm('noahfit.weekdays');
    },
    daysInMonth() {
      const totalDays = new Date(this.year, this.month + 1, 0).getDate();
      const firstDay = new Date(this.year, this.month, 1).getDay();
      const offset = firstDay === 0 ? 6 : firstDay - 1;
      const emptyDays = Array.from({ length: offset }, () => null);
      const days = Array.from({ length: totalDays }, (_, i) => i + 1);
      return [...emptyDays, ...days];
    },
  
    recordedDaysCount() {
      return Object.keys(this.monthCalories).length;
    },
  
    totalMonthCal() {
      return Object.values(this.monthCalories).reduce((sum, val) => sum + (val || 0), 0);
    },
  
    requiredMonthCal() {
      return this.dailycal ? this.dailycal * this.recordedDaysCount : 0;
    },
  
    monthlyDiff() {
      return this.totalMonthCal - this.requiredMonthCal;
    }
  },
  watch: {
    month() {
      this.fetchMonthData();
    },
    year() {
      this.fetchMonthData();
    }
  },
  mounted() {
    this.fetchMonthData();
    eventBus.on("reloadFit", this.fetchMonthData);
    },
    beforeUnmount() {
      eventBus.off("reloadFit", this.fetchMonthData);
    },
  methods: {
    isWeekend(index) {
      const dayIndex = index % 7;
      return dayIndex === 5 || dayIndex === 6;
    },
    isToday(day) {
      return (
        day !== null &&
        day === this.todayDate &&
        this.month === this.todayMonth &&
        this.year === this.todayYear
      );
    },
    async fetchMonthData() {
      try {
        const res = await axios.get('/promo/user/eaten/month', {
          params: {
            year: this.year,
            month: this.month + 1
          }
        });

        const calObj = {};
        if (res.data.records) {
          res.data.records.forEach(item => {
            calObj[item.day] = item.total_cal;
          });
        }

        this.monthCalories = calObj;
        this.dailycal = res.data.dailycal || null;
      } catch (err) { 
        console.error('Aylık veriler alınamadı:', err);
      }
    },
    formatTotal(value) {
      return value.toLocaleString();
    },
    formatCal(value) {
      return value.toLocaleString();
    },
    formatDiff(value) {
      const prefix = value > 0 ? '+' : value < 0 ? '–' : '±';
      return `${prefix} ${Math.abs(value).toLocaleString()} kcal`;
    },
    formatMonthlyDiff(value) {
      const prefix = value > 0 ? '+' : value < 0 ? '–' : '±';
      return `${prefix} ${Math.abs(value).toLocaleString()} kcal`;
    },
  }
};
</script>

<style scoped>
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  width: 100%;
}

.calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid #2b2b2b;
  border-radius: 4px;
  background-color: #060606;
  padding: 20px 9px;
  border: 1px solid #ffffff00;
  min-height: 60px;
  transition: transform 0.3s ease, box-shadow 0.5s ease, color 0.2s ease;
}

.calendar-day:hover:not(.empty) {
  background-color: #1c2b17;
}

.empty {
  cursor: default;
  background: transparent;
  border: none;
}

.weekend {
  background-color: #11160f;
}

.today {
  border: 1px solid #37502e;
}

.calendar-weekday {
  font-weight: 400;
  text-align: center;
  padding: 8px;
  border-bottom: 1px solid #293624;
  background-color: #20301a;
}

.maincon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cal {
  margin-top: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 12px;
}

/* Renkler */
.over {
  color: #ff4c4c; 
}
.under {
  color: #1CC655; 
}
.equal {
  color: #f1c40f; 
}

.diff {
  font-size: 11px;
  margin-top: 2px;
}
@media (max-width: 768px) {
  .diff {
    display: none;
  }
}


.monthly-summary {
  margin-top: 20px;
  padding: 12px;
  background-color: #050505; 
  border-radius: 8px; 
  text-align: left;
  max-width: 100%;
}
.monthly-summary > div {
  margin-bottom: 6px;
}
.monthly-summary .over {
  color: #ff4c4c;
}
.monthly-summary .under {
  color: #1CC655;
}
.monthly-summary .equal {
  color: #f1c40f;
}

</style>
